import React, { useCallback, useEffect } from 'react';
import { useSessionStorage } from 'react-use';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { camelCase } from 'change-case';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useKeycloak } from '@react-keycloak/web';
import InstitutionContext, {
  defaultInstitutionContext,
} from '../contexts/InstitutionContext';
import {
  InitialMyInstitutionsQuery,
  InstitutionSetActiveInstitutionMutation,
} from '#types/generated/graphql';
import SelectInstitutionSplashScreen from './SelectInstitutionSplashScreen';
import { useAppLoading } from '#core/AppLoading';
import Loading from '#coreComponents/Loading';
// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useEnvironment } from '#core/Environment';
import getPermissionToken from '#utils/getPermissionToken';
import { useAuth } from '#core/Auth';
import AccessDeniedErrorScreen from '#coreComponents/ErrorBoundary/AccessDeniedErrorScreen';
import ErrorBoundaryFooter from '#coreComponents/ErrorBoundary/ErrorBoundaryFooter';

const Institution: React.FC<{}> = ({ children }) => {
  const {
    user,
    useElevatedPermissions,
    setPermissionToken,
    isAdmin,
  }: any = useAuth(); // JME2
  const flags = useFlags();
  const userId = user?.[flags[camelCase('auth0Enabled')] ? 'sub' : 'id'];
  const { setIsLoading } = useAppLoading();
  const { apiEndpoint } = useEnvironment();
  const { keycloak } = useKeycloak();

  const [
    savedActiveInstitutionId,
    setSavedActiveInstitutionId,
  ] = useSessionStorage<string | null>(`${userId}-active-institution`, null);

  const {
    loading: initialInstitutionLoading,
    error: initialInstitutionError,
    data: initialInstitutionData,
  } = useQuery<InitialMyInstitutionsQuery>(gql`
    query InitialMyInstitutions {
      myInstitutions(perPage: 1) {
        total
        count
        page
        perPage
        hasMore
        search
        pages
        institutions {
          id
          institutionId
          name
          products {
            peersInfo {
              supportsPeers
              supportsChangingPeers
              peerChangeOpen
              lastPeerChangeDate
              maxPeerCount
              peerChangeIntervalWeeks
            }
          }
        }
      }
    }
  `);

  const [
    validateInstitutionIdMutation,
    { loading: validateInstitutionIdLoading, data: validateInstitutionIdData },
  ] = useMutation<InstitutionSetActiveInstitutionMutation>(
    gql`
      mutation InstitutionSetActiveInstitution($institutionId: String!) {
        setActiveInstitution(id: $institutionId) {
          id
          resource
          institutionId
          name
          logo
          country
          countryCode
          continent
          insightsPreviewUrl
          products {
            name
            peersInfo {
              supportsPeers
              supportsChangingPeers
              peerChangeOpen
              lastPeerChangeDate
              maxPeerCount
              peerChangeIntervalWeeks
              peerChangeDraft {
                userId
                username
                fullName
                institutionIds
                peers {
                  id
                  institutionId
                  name
                  country
                  continent
                  logo
                }
              }
            }
            locations {
              countries
              continents
            }
            institutionGroups {
              name
              institutions {
                id
                institutionId
                name
                country
                continent
                logo
              }
            }
          }
        }
      }
    `,
    {
      onError: () => {},
    },
  );

  const validateInstitutionId = useCallback(
    async (institutionId: string) => {
      await validateInstitutionIdMutation({
        variables: {
          institutionId,
        },
      });
    },
    [validateInstitutionIdMutation],
  );
  const setActiveInstitution = useCallback(
    async (institutionId: string) => {
      await validateInstitutionId(institutionId);
      setSavedActiveInstitutionId(institutionId);
    },
    [setSavedActiveInstitutionId, validateInstitutionId],
  );

  useEffect(() => {
    const fetchPermissionToken = async () => {
      if (keycloak?.token) {
        const token = await getPermissionToken(
          apiEndpoint,
          keycloak?.token,
          useElevatedPermissions,
          savedActiveInstitutionId,
        );

        setPermissionToken(token);
      }
    };

    if (isAdmin) {
      fetchPermissionToken();
    }
  }, [
    keycloak?.token,
    savedActiveInstitutionId,
    apiEndpoint,
    useElevatedPermissions,
    setPermissionToken,
    isAdmin,
  ]);

  useEffect(() => {
    (async () => {
      if (initialInstitutionLoading) {
        return;
      }

      if (savedActiveInstitutionId && savedActiveInstitutionId.length > 0) {
        try {
          await validateInstitutionId(savedActiveInstitutionId);
          setSavedActiveInstitutionId(savedActiveInstitutionId);
        } catch {
          setSavedActiveInstitutionId('');
        }
      } else if (
        // if user has only one institution don't show the select screen
        initialInstitutionData &&
        initialInstitutionData?.myInstitutions.total === 1
      ) {
        const institution =
          initialInstitutionData?.myInstitutions?.institutions?.[0];

        if (institution) {
          try {
            await validateInstitutionId(institution.institutionId);
            setSavedActiveInstitutionId(institution.institutionId);
          } catch {
            // ignore the error
          }
        }
      }
    })();
  }, [
    savedActiveInstitutionId,
    setSavedActiveInstitutionId,
    validateInstitutionId,
    initialInstitutionData,
    initialInstitutionLoading,
  ]);

  useEffect(() => setIsLoading(validateInstitutionIdLoading), [
    setIsLoading,
    validateInstitutionIdLoading,
  ]);

  const clearActiveInstitution = useCallback(
    () => setSavedActiveInstitutionId(null),
    [setSavedActiveInstitutionId],
  );

  if (initialInstitutionLoading || validateInstitutionIdLoading) {
    return <Loading showLogo={true} h="100vh" />;
  }

  if (initialInstitutionError) {
    throw initialInstitutionError;
  }

  if (
    savedActiveInstitutionId &&
    validateInstitutionIdData &&
    validateInstitutionIdData.setActiveInstitution
  ) {
    const institution = validateInstitutionIdData.setActiveInstitution;

    return (
      <InstitutionContext.Provider
        value={{
          hasManyInstitutions:
            initialInstitutionData?.myInstitutions.hasMore || false,
          institution,
          setActiveInstitution,
          clearActiveInstitution,
          apiHeaders: {
            'x-datapoints-institution': institution?.institutionId,
            ...(institution?.institutionId && !useElevatedPermissions
              ? {
                  'x-auth-principal': JSON.stringify({
                    orgId: institution?.institutionId,
                  }),
                }
              : {}),
          },
        }}
      >
        {children}
      </InstitutionContext.Provider>
    );
  }

  if (!validateInstitutionIdData || !savedActiveInstitutionId) {
    return initialInstitutionData?.myInstitutions?.count === 0 ? (
      <AccessDeniedErrorScreen
        text="You do not have access to the DataPoints platform."
        footerComponent={ErrorBoundaryFooter}
      />
    ) : (
      <InstitutionContext.Provider
        value={{
          ...defaultInstitutionContext,
          hasManyInstitutions:
            initialInstitutionData?.myInstitutions?.hasMore || false,
          setActiveInstitution,
        }}
      >
        <SelectInstitutionSplashScreen
          validateInstitution={setActiveInstitution}
        />
      </InstitutionContext.Provider>
    );
  }

  return null;
};

export default Institution;
